import { computed, effect, inject } from '@angular/core';
import { CurrentUser, Employee, EMPLOYEE_REGISTRATION_STATUSES } from '@app/shared/models';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { UserApiService } from '../api';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { APP_ROUTES_PATHS } from '@app/app.routes.model';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPLOYEE_ROUTES_PATHS } from '@app/features/employee/employee.routes.model';
import { AUTH_ROUTES_PATHS } from '@app/auth/auth.routes.model';

const AUTH_TOKEN_STORAGE_KEY = 'authToken';

type AuthState = {
  token: string | null;
  currentUser: CurrentUser | null;
  userProfile: Employee | null;
};

const INITIAL_STATE: AuthState = {
  token: null,
  currentUser: null,
  userProfile: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(INITIAL_STATE),
  withComputed(({ currentUser }) => ({
    hasEmployeeRole: computed(() => currentUser()?.role === 'EMPLOYEE_USER'),
  })),
  withMethods((store, userApiService = inject(UserApiService), router = inject(Router)) => ({
    patch(newPartialState: Partial<AuthState>) {
      patchState(store, newPartialState);
    },
    clear() {
      patchState(store, INITIAL_STATE);
    },
    logout() {
      this.clear();
      router.navigateByUrl(AUTH_ROUTES_PATHS.LOGIN);
    },
    loadCurrUser(): Observable<CurrentUser> {
      return userApiService.getCurrentUser().pipe(tap(user => this.patch({ currentUser: user })));
    },

    loadUserProfile: rxMethod<AuthState['currentUser']>(currUser$ =>
      currUser$.pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        filter(Boolean),
        switchMap(currUser =>
          userApiService.getUserProfile(currUser.id).pipe(
            tap(userProfile => {
              patchState(store, { userProfile });
              if (userProfile.registrationStatus === 'COMPLETED') {
                  router.navigate([APP_ROUTES_PATHS.EMPLOYEE, currUser.id, EMPLOYEE_ROUTES_PATHS.PROFILE]);
                  return;
                }

                const registrationStepPath =
                  userProfile.registrationStatus === EMPLOYEE_REGISTRATION_STATUSES.STEP_TWO
                    ? 'step3'
                    : 'step4';

                router.navigate([APP_ROUTES_PATHS.REGISTRATION, currUser.id, registrationStepPath]);
            }),
            catchError(err => {
              if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.NotFound) {
                router.navigate([APP_ROUTES_PATHS.REGISTRATION, currUser.id, 'step3']);
                return EMPTY;
              }
              return throwError(() => err);
            }),
          ),
        ),
      ),
    ),
  })),
  withHooks({
    onInit: state => {
      const { token } = state;
      const storageToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (storageToken) {
        patchState(state, {
          token: storageToken,
        });
      }
      effect(() =>
        token()
          ? localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token() as string)
          : localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY),
      );
      state.loadUserProfile(state.currentUser);
    },
  }),
);
